import { useState, useMemo, useEffect } from "react";
import { Check, X, Search, ChevronDown } from "lucide-react";

interface HeaderMappingPopoverProps {
  anchor: { x: number; y: number; width: number; height: number };
  imageNatural: { width: number; height: number };
  currentValue: string;
  options: string[];
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function HeaderMappingPopover({
  anchor,
  imageNatural,
  currentValue,
  options,
  onConfirm,
  onCancel,
}: HeaderMappingPopoverProps) {
  const [value, setValue] = useState(currentValue);
  const [filter, setFilter] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Sync state if parent changes (e.g. via OCR pick)
  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  const filteredOptions = useMemo(() => {
    if (!filter) return options;
    const q = filter.toLowerCase();
    return options.filter(opt => opt.toLowerCase().includes(q));
  }, [options, filter]);

  // Allow the user to use a typed value even when nothing matches —
  // OCR header text often won't be present in the predefined attribute
  // list, and forcing a match left them stuck with "No results".
  const trimmedFilter = filter.trim();
  const showCustomOption =
    trimmedFilter.length > 0 &&
    !options.some((opt) => opt.toLowerCase() === trimmedFilter.toLowerCase());

  const leftPct = (anchor.x / imageNatural.width) * 100;
  const topPct = ((anchor.y + anchor.height) / imageNatural.height) * 100;

  return (
    <div
      style={{
        position: "absolute",
        top: `${topPct}%`,
        left: `${leftPct}%`,
        marginTop: "8px",
        zIndex: 100,
      }}
      className="flex w-[260px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-2xl ring-1 ring-black/5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-1.5">
        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
          Map Field
        </label>
        
        {/* Searchable Select Container */}
        <div className="relative">
          <div 
            className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="truncate text-xs text-gray-900">
              {value || "Map Field..."}
            </span>
            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>

          {isOpen && (
            <div className="absolute left-0 top-full mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-xl z-[110]">
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    autoFocus
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search attributes..."
                    className="w-full rounded-md border border-gray-200 py-1.5 pl-7 pr-2 text-xs focus:border-blue-500 focus:outline-none"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              <ul className="max-h-48 overflow-y-auto py-1">
                {filteredOptions.map((opt) => (
                  <li key={opt}>
                    <button
                      type="button"
                      onClick={() => {
                        setValue(opt);
                        setIsOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs hover:bg-blue-50 transition-colors ${
                        value === opt ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {opt}
                      {value === opt && <Check className="h-3 w-3 text-blue-600" />}
                    </button>
                  </li>
                ))}
                {showCustomOption && (
                  <li className="border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => {
                        setValue(trimmedFilter);
                        setFilter("");
                        setIsOpen(false);
                      }}
                      className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-xs text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      <span className="text-gray-400">Use:</span>
                      <span className="font-medium">{trimmedFilter}</span>
                    </button>
                  </li>
                )}
                {filteredOptions.length === 0 && !showCustomOption && (
                  <li className="px-3 py-2 text-xs text-gray-500">No results</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => onConfirm(value)}
          disabled={!value}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-white shadow-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors"
          title="Confirm"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          onClick={onCancel}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500 text-white shadow-sm hover:bg-rose-600 transition-colors"
          title="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
